import { Button, Css, ModalBody, useComputed } from "@homebound/beam";
import { useSuspense } from "@rest-hooks/react";
import { useState } from "react";
import { CustomLoadingBoundary } from "src/components/LoadingBoundary";
import { ModalFooterWTip, SectionTitle } from "src/components/outdatedReadyPlanModal/components/atoms";
import { useController, useReaction } from "src/hooks";
import { ReadyPlan, SaveUnderwritingReportEndpoint, UnderwritingReport } from "src/routes/cma/endpoints/reports";
import {
  BlueprintReadyPlanComputeData,
  BlueprintReadyPlanComputeDataEndpoint,
} from "src/routes/cma/steps/ready-plan/v2/endpoints/BlueprintReadyPlanComputeDataEndpoint";
import {
  BlueprintOptionGroupsEndpoint,
  ReadyPlanOption,
} from "src/routes/cma/steps/ready-plan/v2/endpoints/BlueprintReadyPlanOptionsEndpoint";
import { RenderCostChanges } from "./RenderCostChanges";
import { RenderProgramChanges } from "./RenderProgramChanges";

export function PlanTemplateOutdatedContent({
  report,
  onChooseNewRp,
  devId,
  currentIncrement,
}: {
  onChooseNewRp: () => void;
  report: UnderwritingReport;
  devId: string;
  currentIncrement: string;
}) {
  const readyPlan = report.ready_plans![0];
  const [missingOptions, setMissingOptions] = useState<string[]>();

  const { optionGroups } = useSuspense(BlueprintOptionGroupsEndpoint, {
    devId,
    bp_ready_plan_id: readyPlan.bp_ready_plan_id!,
  });

  useReaction(
    () => ({
      allRPOptions: optionGroups?.flatMap((o) => o.options),
    }),
    ({ allRPOptions }: { allRPOptions: ReadyPlanOption[] }) => {
      const missingConfiguredOptions = readyPlan.bp_ready_plan_option_ids?.filter(
        (rpOptionId) => !allRPOptions?.some((aRPO) => aRPO.id === rpOptionId),
      );
      setMissingOptions(missingConfiguredOptions ?? []);
    },
    { fireImmediately: true },
    [optionGroups],
  );

  function RenderMissingOptions() {
    const missingConfiguredOptions = readyPlan.bp_ready_plan_options_json?.optionGroups
      ?.flatMap((o) => o.options)
      .filter((o) => missingOptions?.includes(o.id));

    return (
      <>
        <SectionTitle label="The following options are no longer available:" success={false} />
        <ul>
          {missingConfiguredOptions?.map(({ id, displayName, type }) => {
            return (
              <li key={id}>
                [{type.name}] {displayName}
              </li>
            );
          })}
        </ul>
        Please reconfigure your ready plan to ensure accurate pricing and comp generation.
      </>
    );
  }

  function RenderProgramDataCheck() {
    return (
      <>
        <SectionTitle label="All options accounted for" />
        <CustomLoadingBoundary fallback={<SectionTitle label="Comparing Program Data" loading />}>
          <ProgramDataCosts
            report={report}
            devId={devId}
            currentIncrement={currentIncrement}
            onChooseNewRp={onChooseNewRp}
          />
        </CustomLoadingBoundary>
      </>
    );
  }

  return (
    <>
      <ModalBody>
        <div css={Css.df.fdc.gap1.$}>
          {missingOptions?.length === 0 ? <RenderProgramDataCheck /> : <RenderMissingOptions />}
        </div>
      </ModalBody>
      {(missingOptions?.length ?? 0) > 0 && (
        <ModalFooterWTip>
          <Button label="Go To Plans" onClick={onChooseNewRp} variant="primary" />
        </ModalFooterWTip>
      )}
    </>
  );
}

function ProgramDataCosts({
  report,
  devId,
  currentIncrement,
  onChooseNewRp,
}: {
  report: UnderwritingReport;
  devId: string;
  currentIncrement: string;
  onChooseNewRp: () => void;
}) {
  const readyPlan = report.ready_plans![0];
  const { fetch } = useController();
  const computeData = useSuspense(BlueprintReadyPlanComputeDataEndpoint, {
    devId: devId,
    bp_ready_plan_id: readyPlan.bp_ready_plan_id!,
    options: readyPlan.bp_ready_plan_option_ids!,
  });

  const programDataChanges = useComputed(() => {
    return getProgramDataChanges(computeData!, readyPlan);
  }, [computeData, readyPlan]);

  // Apply the changes and refresh window
  async function updateRPCosts() {
    const updatedReadyPlan = {
      ...readyPlan,
      bp_ready_plan_version: currentIncrement,
      direct_hard_cost_in_cents: computeData.computeReadyPlanCost!.directHardCostInCents,
      indirect_hard_cost_in_cents: computeData.computeReadyPlanCost!.indirectHardCostInCents,
      soft_cost_in_cents: computeData.computeReadyPlanCost!.softCostInCents,
      hard_costs_per_sqft:
        computeData.computeReadyPlanCost!.directHardCostInCents / 100 / computeData.computeProgramData!.sellableSqft!,
    };

    await fetch(SaveUnderwritingReportEndpoint, {
      report: {
        ...report,
        ready_plans: [updatedReadyPlan],
      },
      versionId: report.id,
    });

    window.location.reload();
  }

  return (
    <>
      <RenderProgramChanges programDataChanges={programDataChanges} />
      <RenderCostChanges computeData={computeData} readyPlan={readyPlan} />
      <ModalFooterWTip>
        {programDataChanges.length > 0 ? (
          <Button label="Go To Plans" onClick={onChooseNewRp} variant="primary" />
        ) : (
          <Button label="Confirm & Apply" onClick={updateRPCosts} variant="primary" />
        )}
      </ModalFooterWTip>
    </>
  );
}

// stored property-api entity -> computed blueprint-ready-plan-compute-data response
export const rpPdMap = {
  above_ground_sqft: "sellableAboveGroundSqft",
  below_ground_sqft: "unfinishedBelowGroundSqft",
  num_baths: "fullBaths",
  num_bedrooms: "bedrooms",
  num_garage_attached: "garageAttached",
  num_garage_port: "garagePort",
  num_half_baths: "halfBaths",
  num_stories: "stories",
  depth_in_inches: "depthInInches",
  width_in_inches: "widthInInches",
  sellable_basement_sqft: "sellableBelowGroundSqft",
  sellable_sqft: "sellableSqft",
};

export function getProgramDataChanges(computeData: BlueprintReadyPlanComputeData, readyPlan: ReadyPlan) {
  const { computeProgramData: computedPD } = computeData!;
  let changedProgramData: { label: string; old: number; new: number }[] = [];
  for (const [storedKey, computedKey] of Object.entries(rpPdMap)) {
    // @ts-ignore: silence index issues from mix n match interfaces/types/classes
    const storedValue: number = readyPlan[storedKey];
    // @ts-ignore: silence index issues from mix n match interfaces/types/classes
    const newComputedValue: number = computedPD[computedKey] === null ? 0 : computedPD[computedKey];
    if (storedValue !== newComputedValue) {
      changedProgramData.push({ label: storedKey, old: storedValue, new: newComputedValue });
    }
  }

  return changedProgramData;
}
